import AsyncStorage from '@react-native-async-storage/async-storage';

/**
 * The id of a Skylight push that has not finished yet.
 *
 * The push is asynchronous precisely so it can outlive the request that started
 * it — 20-30s of sequential writes, on a function that may be killed at 60s.
 * That buys nothing if the *view* of it dies on the next remount: leaving
 * Settings and coming back would show a ready button while the push was still
 * running, which is the synchronous design's behaviour at the asynchronous
 * design's price.
 *
 * So the id is held here across mounts, and the button adopts it on boot.
 * Cleared the moment the job reaches a terminal state, so a stale id can never
 * resurrect an old result.
 *
 * Modelled on `pendingImport.ts`, except the read does NOT consume: polling has
 * to survive several reads, and it is the terminal state — not the first
 * glance — that ends the entry's life.
 */

const KEY = 'paprika_pending_skylight_job';

/**
 * A push takes 20-30s, and the status endpoint calls a job that stopped moving
 * dead after 90s. An id older than this is not something anyone is still
 * waiting on — it survives a crash mid-push, not a night's sleep.
 */
const MAX_AGE_MS = 30 * 60 * 1000;

/**
 * How long a stored id is allowed to 404 before we conclude no job was ever
 * created for it.
 *
 * The client names the job and starts polling on the tap, so the first poll
 * usually arrives BEFORE the POST has created anything: that request still has
 * to cross the network, cold-start the function, verify the token, resolve the
 * household (a Firestore read) and only then call `createJob`. A 404 in that
 * window means "not yet", not "never" — and treating it as "never" would erase
 * the id while the push was still writing to the Calendar.
 *
 * 30s is chosen against that cold path with room to spare, and stays well under
 * the server's 90s staleness rule, so the two never disagree about a job.
 */
export const SKYLIGHT_JOB_CREATION_GRACE_MS = 30_000;

interface StoredPendingPush {
  jobId: string;
  savedAt: number;
}

export async function setPendingSkylightJob(jobId: string): Promise<void> {
  try {
    const payload: StoredPendingPush = { jobId, savedAt: Date.now() };
    await AsyncStorage.setItem(KEY, JSON.stringify(payload));
  } catch {
    // Losing the id costs the user the progress view, not the push. Never let
    // that take the button down with it.
  }
}

/** The unfinished push to adopt on mount, or null. Does not clear. */
export async function readPendingSkylightJob(): Promise<string | null> {
  try {
    const raw = await AsyncStorage.getItem(KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as StoredPendingPush;
    if (!parsed?.jobId || typeof parsed.savedAt !== 'number') return null;
    if (Date.now() - parsed.savedAt > MAX_AGE_MS) {
      await AsyncStorage.removeItem(KEY);
      return null;
    }
    return parsed.jobId;
  } catch {
    return null;
  }
}

/**
 * How long ago the pending push was started, in ms, or null if there is none.
 *
 * Not bounded by `MAX_AGE_MS` the way `readPendingSkylightJob` is: this answers
 * "how old is the entry", and the caller decides what that means.
 */
export async function pendingSkylightJobAge(): Promise<number | null> {
  try {
    const raw = await AsyncStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredPendingPush;
    if (!parsed?.jobId || typeof parsed.savedAt !== 'number') return null;
    return Date.now() - parsed.savedAt;
  } catch {
    return null;
  }
}

export async function clearPendingSkylightJob(): Promise<void> {
  try {
    await AsyncStorage.removeItem(KEY);
  } catch {
    // Nothing to recover; the age check above bounds a stranded entry anyway.
  }
}
